import { useCallback, useRef, useState } from "react";

/**
 * Width of an element in CSS pixels, followed by a ResizeObserver where there is
 * one. jsdom has none and measures 0, so there the width stays at `fallback`.
 *
 * Used as an SVG's viewBox width so one user unit is one pixel: the x-scale
 * stretches with the container and text keeps its intended size at any width.
 */
export function useWidth(fallback: number): [(el: Element | null) => void, number] {
  const [width, setWidth] = useState(fallback);
  const observer = useRef<ResizeObserver | null>(null);
  const ref = useCallback((el: Element | null) => {
    observer.current?.disconnect();
    observer.current = null;
    if (!el) return;
    const measure = () => {
      const w = Math.round(el.getBoundingClientRect().width);
      if (w > 0) setWidth(w);
    };
    measure();
    if (typeof ResizeObserver !== "undefined") {
      observer.current = new ResizeObserver(measure);
      observer.current.observe(el);
    }
  }, []);
  return [ref, width];
}
